import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from './CustomButton';

/**
 * One editable unit of a settings page: a card that shows its saved values and
 * is changed only after "Bearbeiten" (F, PO 2026-10-11; rule in
 * `docs/design-decisions.md` › Bearbeiten und Speichern).
 *
 * - View: the saved values and a "Bearbeiten" button (hidden when read-only).
 * - Edit: the form, "Abbrechen" and "Speichern". Speichern is enabled only when
 *   something changed and the form is valid, and is disabled while saving.
 *   After a successful save the caller closes the unit (`useEditableDraft`).
 *
 * A unit is whatever is done in one go: per use case where settings are per
 * use case, for the whole tenant where they are not. The frame does not decide
 * that; the page does, by how many units it renders.
 */
interface EditableSectionProps {
    title: string;
    description?: ReactNode;
    /** Anchor id for deep links. */
    id?: string;
    editing: boolean;
    /** No edit rights: the view only, no "Bearbeiten". */
    readOnly?: boolean;
    isDirty: boolean;
    saving: boolean;
    /** Form validity; Speichern also needs a change. Defaults to valid. */
    canSave?: boolean;
    /** Shown above the buttons while editing — e.g. the server's error. */
    error?: string | null;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    /** What the unit shows when not editing. */
    view: ReactNode;
    /** The form, rendered only while editing. */
    children: ReactNode;
}

const EditableSection = ({
    title, description, id, editing, readOnly = false, isDirty, saving, canSave = true, error,
    onEdit, onCancel, onSave, view, children,
}: EditableSectionProps) => {
    const { t } = useTranslation();
    return (
        <section id={id} className={'bg-text-contrast rounded-[5px] shadow-sm p-6'}>
            <div className={'flex items-start justify-between gap-4 mb-4'}>
                <div className={'min-w-0'}>
                    <h2 className={'text-lg font-semibold text-foreground mb-1'}>{title}</h2>
                    {description && <div className={'text-sm text-foreground/60'}>{description}</div>}
                </div>
                {!editing && !readOnly && (
                    <div className={'shrink-0'}>
                        <CustomButton title={t('common.edit')} isFilled={false} onClick={onEdit} />
                    </div>
                )}
            </div>

            {editing ? (
                <div className={'flex flex-col gap-4'}>
                    {children}
                    {error && <p className={'text-sm text-foreground'}>{error}</p>}
                    <div className={'flex justify-end gap-3'}>
                        <CustomButton title={t('common.cancel')} isFilled={false} onClick={onCancel} />
                        <CustomButton
                            title={t('common.save')}
                            onClick={onSave}
                            loading={saving}
                            disabled={!isDirty || !canSave || saving}
                        />
                    </div>
                </div>
            ) : view}
        </section>
    );
};

export default EditableSection;
